import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import {
  useListUserDqlQueries,
  useFavoriteUserDqlQuery,
  useRemoveUserDqlQuery
} from '../../../../../api/denali'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import { useConfigColumns } from '../../../../../hooks/useConfigColumns'
import AdminPage from '../../../admin/shared/AdminPage'
import OperationalTable from '../../../../organisms/OperationalTable'
import FlexRow from '../../../../molecules/FlexRow'
import Loading from '../../../../molecules/Loading'
import SydLinkButton from '../../../../commonDesign/LinkButton'
import FavoriteStarButton from '../QueryTool/FavoriteStarButton'

const useStyles = makeStyles(() => ({
  deleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '4px 8px',
    color: 'var(--color-contrast-neutral-300)',
    '&:hover': {
      color: 'var(--color-error-500)'
    }
  },
  processing: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  description: {
    maxWidth: '350px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}))

function SavedQueriesHome ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const { formatter } = useFormattingContext()
  const [favoritingId, setFavoritingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const { data, isLoading } = useListUserDqlQueries()
  const { mutate: toggleFavorite } = useFavoriteUserDqlQuery()
  const { mutate: removeQuery } = useRemoveUserDqlQuery()

  const handleFavorite = useCallback((e, query) => {
    e.stopPropagation()
    setFavoritingId(query.userDqlQueryId)
    toggleFavorite(
      { userDqlQueryId: query.userDqlQueryId, isFavorite: !query.isFavorite },
      { onSettled: () => setFavoritingId(null) }
    )
  }, [toggleFavorite])

  const handleDelete = useCallback((e, query) => {
    e.stopPropagation()
    if (window.confirm(`Delete "${query.name}"? This cannot be undone.`)) {
      setDeletingId(query.userDqlQueryId)
      removeQuery(
        { userDqlQueryId: query.userDqlQueryId },
        { onSettled: () => setDeletingId(null) }
      )
    }
  }, [removeQuery])

  /* eslint-disable react/prop-types */
  const columnConfig = useMemo(() => ({
    columns: [
      {
        id: 'isFavorite',
        accessor: 'isFavorite',
        Header: '',
        disableSortBy: true,
        width: 40,
        Cell: ({ row }) => (
          <FavoriteStarButton
            isFavorite={row.original.isFavorite}
            loading={favoritingId === row.original.userDqlQueryId}
            onClick={(e) => handleFavorite(e, row.original)}
          />
        )
      },
      { id: 'name', accessor: 'name', Header: 'Name' },
      {
        id: 'description',
        accessor: 'description',
        Header: 'Description',
        Cell: ({ value }) => (
          <div className={classes.description}>{value || '\u2014'}</div>
        )
      },
      { id: 'createdDate', accessor: 'createdDate', Header: 'Created', format: 'timestamp' },
      { id: 'modifiedDate', accessor: 'modifiedDate', Header: 'Modified', format: 'timestamp' },
      {
        id: 'actions',
        accessor: 'userDqlQueryId',
        Header: '',
        disableSortBy: true,
        width: 50,
        Cell: ({ row }) => {
          const query = row.original
          const isProcessing = deletingId === query.userDqlQueryId
          return (
            <button
              className={`${classes.deleteBtn} ${isProcessing ? classes.processing : ''}`}
              onClick={(e) => handleDelete(e, query)}
              disabled={isProcessing}
              title='Delete query'
            >
              {isProcessing ? '\u2026' : '\u2715'}
            </button>
          )
        }
      }
    ],
    defaultSort: [{ id: 'modifiedDate', desc: true }]
  }), [classes, favoritingId, deletingId, handleFavorite, handleDelete])
  /* eslint-enable react/prop-types */

  const _columns = useConfigColumns({
    columnsConfig: columnConfig,
    format: formatter
  })

  if (isLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FlexRow style={{ justifyContent: 'flex-end' }}>
            <SydLinkButton to={`${root}/analysis/catalog/query`} icon='add' size='sm' variant='primary'>
              New Query
            </SydLinkButton>
          </FlexRow>
        </Grid>
        <Grid item xs={12}>
          <OperationalTable
            variant='v2'
            itemName='Saved Queries'
            columns={_columns.columns}
            defaultSort={_columns.defaultSort}
            data={data?.queries || []}
            // eslint-disable-next-line react/prop-types
            onRowClick={(row) => history.push(`${root}/analysis/catalog/query?queryId=${row.original.userDqlQueryId}`)}
          />
        </Grid>
      </Grid>
    </AdminPage>
  )
}

SavedQueriesHome.propTypes = {
  root: PropTypes.string.isRequired
}

export default SavedQueriesHome
